// #region Imports
import { useEffect, useState, useCallback } from 'react';
import clientesApi from '../services/clientesApi';
import { clienteFromBackend } from '../pages/Clientes/cliente-utils';
// #endregion

export default function useClientes() {
	// #region Estado
	const [clientes, setClientes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	// #endregion

	// #region Efeitos (carga inicial)
	useEffect(() => {
		let active = true;
		async function load() {
			setLoading(true);
			setError(null);
			try {
				const raw = await clientesApi.listAll();
				if (!active) return;
				setClientes(raw.map(clienteFromBackend));
			} catch (e) {
				if (active) setError(e);
			} finally {
				if (active) setLoading(false);
			}
		}
		load();
		return () => { active = false; };
	}, []);
	// #endregion

	// #region Handlers principais (listar/obter/criar/atualizar/remover)
	const listar = useCallback(({ q = '', page = 1, pageSize = 10, sexo, saldo } = {}) => {
		const normalized = q.trim().toLowerCase();
		let filtered = clientes;
		if (normalized) {
			filtered = filtered.filter(c =>
				(c.nome || '').toLowerCase().includes(normalized) ||
				(c.cpf || '').toLowerCase().includes(normalized) ||
				(c.telefone || '').toLowerCase().includes(normalized)
			);
		}
		if (sexo && sexo !== 'TODOS') filtered = filtered.filter(c => c.sexo === sexo);
		if (saldo && saldo !== 'TODOS') {
			if (saldo === 'EM_ABERTO') filtered = filtered.filter(c => (c.saldo_devedor || 0) > 0);
			if (saldo === 'EM_DIA') filtered = filtered.filter(c => (c.saldo_devedor || 0) <= 0);
		}
		const total = filtered.length;
		const start = (page - 1) * pageSize;
		const data = filtered.slice(start, start + pageSize);
		return { data, total, page, pageSize };
	}, [clientes]);

	const obter = useCallback((id) => clientes.find(c => c.id === id) || null, [clientes]);

	const criar = useCallback(async (payload) => {
		const dto = await clientesApi.create(payload);
		const inst = clienteFromBackend(dto);
		setClientes(prev => [inst, ...prev]);
		return inst;
	}, []);

	const atualizar = useCallback(async (id, payload) => {
		const dto = await clientesApi.update(id, payload);
		if (!dto) return null;
		const inst = clienteFromBackend(dto);
		setClientes(prev => prev.map(c => (c.id === id ? inst : c)));
		return inst;
	}, []);

	const remover = useCallback(async (id) => {
		await clientesApi.remove(id);
		setClientes(prev => prev.filter(c => c.id !== id));
	}, []);
	// #endregion

	// #region Helpers
	function computeStats(arr) {
		let total = arr.length;
		let comSaldo = 0;
		let totalSaldo = 0;
		arr.forEach(c => {
			const v = c.saldo_devedor || 0;
			if (v > 0) { comSaldo++; totalSaldo += v; }
		});
		return { totalClientes: total, totalComSaldo: comSaldo, totalValorReceber: totalSaldo };
	}
	// #endregion

	// #region Export
	return {
		clientes,
		loading,
		error,
		listar,
		obter,
		criar,
		atualizar,
		remover,
		computeStats,
	};
	// #endregion
}